import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Popover,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import {
  FoodCandidate,
  FoodMappingList,
  FoodMappingRef,
  FoodMappingRow,
  FoodMappingSaveItem,
  MappingStatus,
  PLATFORM_COLORS,
  PLATFORM_LABELS,
  foodPlatformMappingService,
} from '../services/foodPlatformMappingService';

interface CategoryOption {
  id: string;
  name: string;
}

interface BranchOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Şube görünümü (paket sipariş ekranı). Boşsa şirket görünümü (Tüm Siparişler). */
  branchId?: string | null;
  /** Şirket görünümünde şube seçimi için. */
  branches?: BranchOption[];
  categories?: CategoryOption[];
  /** Kaydedilince (ör. yazdırma kategorileri yenilensin). */
  onSaved?: () => void;
}

type Pending = { action: 'map' | 'ignore' | 'reset'; scope: 'account' | 'branch'; item?: FoodCandidate | null };
type StatusFilter = 'all' | MappingStatus;

const rowKey = (r: FoodMappingRow) => `${r.platform}|${r.accountKey}|${r.matchKey}`;

const STATUS_COLOR: Record<MappingStatus, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  auto: 'success',
  manual: 'info',
  suggested: 'warning',
  unmatched: 'error',
  ignored: 'default',
};

const scoreColor = (score: number, s: { matchThreshold: number; suggestThreshold: number }) =>
  score >= s.matchThreshold ? 'success.main' : score >= s.suggestThreshold ? 'warning.main' : 'error.main';

const FoodProductMappingModal: React.FC<Props> = ({ open, onClose, branchId, branches = [], categories = [], onSaved }) => {
  const { t } = useTranslation('salesSystem');
  const k = (key: string, o?: Record<string, unknown>) => t(`orderManagement.productMapping.${key}`, o);

  // Şirket görünümünde seçilen şube ('' = bütün şirket)
  const [viewBranch, setViewBranch] = useState<string>('');
  const effectiveBranch = branchId || viewBranch || null;
  const isCompanyView = !branchId;

  const [data, setData] = useState<FoodMappingList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, Pending>>({});
  const [overrideOpen, setOverrideOpen] = useState<Set<string>>(new Set());
  const [platform, setPlatform] = useState<string>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState<number>(85);
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Ürün seçici (tek bir açılır pencere, satır başına değil)
  const [picker, setPicker] = useState<{ anchor: HTMLElement; row: FoodMappingRow; scope: 'account' | 'branch' } | null>(null);
  const [pickerOptions, setPickerOptions] = useState<FoodCandidate[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const searchTimer = useRef<number | null>(null);
  const searchSeq = useRef(0);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: FoodMappingRow } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await foodPlatformMappingService.list(effectiveBranch);
      setData(d);
      setThreshold(d.settings.matchThreshold);
    } catch (e: any) {
      setError(e?.message || k('loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBranch]);

  useEffect(() => {
    if (!open) return;
    setPending({});
    setOverrideOpen(new Set());
    setNotice(null);
    void load();
  }, [open, load]);

  const categoryName = useCallback((id?: string | null) => (id ? categories.find(c => c.id === id)?.name ?? null : null), [categories]);
  const accountOf = useCallback((r: FoodMappingRow) => data?.accounts.find(a => a.accountKey === r.accountKey && a.platform === r.platform), [data]);

  /** Bu satırda yapılan değişiklik hangi kapsama yazılır. */
  const editScope = useCallback((r: FoodMappingRow): 'account' | 'branch' | null => {
    if (isCompanyView && !viewBranch) return 'account';
    const acc = accountOf(r);
    const ownAccount = !!acc && acc.branchIds.length === 1;
    if (ownAccount) return 'account';
    if (r.branch || overrideOpen.has(rowKey(r))) return 'branch';
    return null; // şirketin eşleşmesi: önce "Bu şubede değiştir"
  }, [isCompanyView, viewBranch, accountOf, overrideOpen]);

  /** Ekranda görünen eşleşme: bekleyen değişiklik, yoksa şube, yoksa hesap. */
  const shown = useCallback((r: FoodMappingRow): { ref: FoodMappingRef | null; status: MappingStatus; itemName?: string | null; itemId?: string | null; score: number; source: 'pending' | 'branch' | 'account' } => {
    const p = pending[rowKey(r)];
    const base = r.branch ?? r.account ?? null;
    if (p) {
      if (p.action === 'ignore') return { ref: base, status: 'ignored', score: 0, source: 'pending' };
      if (p.action === 'reset') {
        const acc = r.account;
        return { ref: acc ?? null, status: acc?.status ?? 'unmatched', itemName: acc?.inventoryItemName, itemId: acc?.inventoryItemId, score: acc?.score ?? 0, source: 'account' };
      }
      return { ref: base, status: 'manual', itemName: p.item?.name, itemId: p.item?.inventoryItemId, score: p.item?.score ?? 0, source: 'pending' };
    }
    return {
      ref: base,
      status: base?.status ?? 'unmatched',
      itemName: base?.inventoryItemName,
      itemId: base?.inventoryItemId,
      score: base?.score ?? 0,
      source: r.branch ? 'branch' : 'account',
    };
  }, [pending]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const platformCounts = useMemo(() => {
    const m: Record<string, number> = {};
    rows.forEach(r => { m[r.platform] = (m[r.platform] || 0) + 1; });
    return m;
  }, [rows]);
  const statusCounts = useMemo(() => {
    const m: Record<string, number> = {};
    rows.filter(r => platform === 'all' || r.platform === platform).forEach(r => {
      const s = shown(r).status;
      m[s] = (m[s] || 0) + 1;
    });
    return m;
  }, [rows, platform, shown]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return rows.filter(r =>
      (platform === 'all' || r.platform === platform)
      && (status === 'all' || shown(r).status === status)
      && (!q || r.externalName.toLocaleLowerCase('tr-TR').includes(q) || (shown(r).itemName || '').toLocaleLowerCase('tr-TR').includes(q)));
  }, [rows, platform, status, search, shown]);

  const suggestionRows = useMemo(
    () => rows.filter(r => !pending[rowKey(r)] && shown(r).status === 'suggested' && shown(r).itemId && editScope(r) !== null),
    [rows, pending, shown, editScope],
  );

  const pendingCount = Object.keys(pending).length;

  // ---------------- Değişiklikler ----------------

  const setRowPending = (r: FoodMappingRow, p: Pending | null) => {
    setPending(prev => {
      const next = { ...prev };
      if (p) next[rowKey(r)] = p; else delete next[rowKey(r)];
      return next;
    });
  };

  const approveSuggestions = () => {
    setPending(prev => {
      const next = { ...prev };
      suggestionRows.forEach(r => {
        const s = shown(r);
        const scope = editScope(r);
        if (!scope || !s.itemId) return;
        next[rowKey(r)] = { action: 'map', scope, item: { inventoryItemId: s.itemId, name: s.itemName || '', score: s.score } };
      });
      return next;
    });
  };

  const openPicker = (anchor: HTMLElement, r: FoodMappingRow) => {
    const scope = editScope(r);
    if (!scope) return;
    setPicker({ anchor, row: r, scope });
    setPickerOptions(r.suggestions);
  };

  const searchCandidates = (text: string) => {
    if (!picker) return;
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    const row = picker.row;
    const reqId = ++searchSeq.current;
    if (!text.trim()) { setPickerLoading(false); setPickerOptions(row.suggestions); return; }
    // Eski öneriler aranan metinle ilgisiz; sonuç gelene kadar listeyi boşalt
    setPickerOptions([]);
    setPickerLoading(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const found = await foodPlatformMappingService.candidates({
          branchId: effectiveBranch, platform: row.platform, accountKey: row.accountKey, search: text,
        });
        if (reqId === searchSeq.current) setPickerOptions(found);
      } catch {
        if (reqId === searchSeq.current) setPickerOptions([]);
      } finally {
        if (reqId === searchSeq.current) setPickerLoading(false);
      }
    }, 250);
  };

  const save = async () => {
    if (!data || pendingCount === 0) return;
    setSaving(true);
    setError(null);
    try {
      const items: FoodMappingSaveItem[] = rows
        .filter(r => pending[rowKey(r)])
        .map(r => {
          const p = pending[rowKey(r)];
          return {
            platform: r.platform,
            accountKey: r.accountKey,
            externalProductId: r.externalProductId,
            externalName: r.externalName,
            externalVariant: r.externalVariant,
            scope: p.scope,
            branchId: p.scope === 'branch' ? effectiveBranch : null,
            action: p.action,
            inventoryItemId: p.item?.inventoryItemId ?? null,
          };
        });
      await foodPlatformMappingService.saveBatch(items);
      setNotice(k('saved', { count: items.length }));
      setPending({});
      setOverrideOpen(new Set());
      onSaved?.();
      await load();
    } catch (e: any) {
      setError(e?.message || k('saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveThreshold = async () => {
    if (!data) return;
    setSavingThreshold(true);
    setError(null);
    try {
      const suggest = Math.min(data.settings.suggestThreshold, threshold - 5);
      await foodPlatformMappingService.saveSettings(threshold, Math.max(0, suggest));
      setNotice(k('thresholdSaved', { value: threshold }));
      await load();
    } catch (e: any) {
      setError(e?.message || k('saveError'));
    } finally {
      setSavingThreshold(false);
    }
  };

  // ---------------- Görünüm ----------------

  const settings = data?.settings ?? { matchThreshold: 85, suggestThreshold: 65 };
  const showAccountLabel = (data?.accounts.length ?? 0) > 1;

  const sourceChip = (r: FoodMappingRow) => {
    const s = shown(r);
    const acc = accountOf(r);
    if (isCompanyView && !viewBranch) {
      if (r.branchOverrides.length === 0) return null;
      return (
        <Tooltip title={r.branchOverrides.map(o => `${o.branchName || o.branchId} → ${o.inventoryItemName || k('status.ignored')}`).join('\n')}>
          <Chip size="small" color="warning" variant="outlined" label={k('overriddenBy', { count: r.branchOverrides.length })} />
        </Tooltip>
      );
    }
    if (acc && acc.branchIds.length === 1) return <Chip size="small" variant="outlined" label={k('source.ownAccount')} />;
    if (s.source === 'branch' || pending[rowKey(r)]?.scope === 'branch') {
      const by = r.branch?.updatedByName;
      return <Chip size="small" color="info" variant="outlined" label={by ? `${k('source.branch')} · ${by}` : k('source.branch')} />;
    }
    return <Chip size="small" variant="outlined" label={k('source.company')} />;
  };

  const statusChip = (r: FoodMappingRow) => {
    const s = shown(r);
    const by = s.ref?.updatedByName;
    const label = s.status === 'manual' && by && s.source !== 'pending' ? `${k(`status.${s.status}`)} · ${by}` : k(`status.${s.status}`);
    return <Chip size="small" color={STATUS_COLOR[s.status]} variant={s.status === 'ignored' ? 'outlined' : 'filled'} label={label} />;
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '90vh' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
      {/* Başlık */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <LinkIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>{k('title')}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {isCompanyView ? k('subtitleCompany') : k('subtitleBranch', { branch: data?.branchName || '' })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </Box>

      {/* Kapsam ve eşik */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' }, gap: 1.5, px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {isCompanyView ? (
            <FormControl size="small" sx={{ minWidth: 240, bgcolor: 'background.paper' }}>
              <InputLabel id="fpm-branch">{k('viewLabel')}</InputLabel>
              <Select labelId="fpm-branch" label={k('viewLabel')} value={viewBranch} onChange={(e) => { setViewBranch(String(e.target.value)); setPending({}); }}>
                <MenuItem value="">{k('wholeCompany')}</MenuItem>
                {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </Select>
            </FormControl>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700}>
              {effectiveBranch
                ? (data?.branchHasPriceList ? k('candidatesBranchList') : k('candidatesCompany'))
                : k('candidatesCompanyAll')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {effectiveBranch ? k('scopeHintBranch') : k('scopeHintCompany')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">{isCompanyView ? k('threshold') : k('thresholdFromCompany')}</Typography>
            <Typography variant="body2" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>%{threshold}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Slider size="small" min={60} max={100} step={1} value={threshold} disabled={!isCompanyView || savingThreshold}
              onChange={(_, v) => setThreshold(v as number)} aria-label={k('threshold')} />
            {isCompanyView && threshold !== settings.matchThreshold && (
              <Button size="small" variant="contained" onClick={() => void saveThreshold()} disabled={savingThreshold}>
                {savingThreshold ? <CircularProgress size={16} color="inherit" /> : k('applyThreshold')}
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {k('bands', { suggest: settings.suggestThreshold, match: settings.matchThreshold })}
          </Typography>
        </Box>
      </Box>

      {/* Filtreler */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Chip label={`${k('all')} ${rows.length}`} color={platform === 'all' ? 'primary' : 'default'} variant={platform === 'all' ? 'filled' : 'outlined'} onClick={() => setPlatform('all')} size="small" />
        {Object.keys(PLATFORM_LABELS).filter(p => platformCounts[p]).map(p => (
          <Chip key={p} size="small" onClick={() => setPlatform(p)} variant={platform === p ? 'filled' : 'outlined'} color={platform === p ? 'primary' : 'default'}
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PLATFORM_COLORS[p] }} />
                {PLATFORM_LABELS[p]} {platformCounts[p]}
              </Box>
            )} />
        ))}
        <Box sx={{ width: 12 }} />
        {(['auto', 'manual', 'suggested', 'unmatched', 'ignored'] as MappingStatus[]).map(s => (
          <Chip key={s} size="small" onClick={() => setStatus(status === s ? 'all' : s)} color={status === s ? STATUS_COLOR[s] : 'default'}
            variant={status === s ? 'filled' : 'outlined'} label={`${k(`status.${s}`)} ${statusCounts[s] || 0}`} />
        ))}
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder={k('search')} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: { xs: '100%', sm: 220 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button size="small" variant="outlined" disabled={suggestionRows.length === 0} onClick={approveSuggestions}>
          {k('approveSuggestions', { count: suggestionRows.length })}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ borderRadius: 0 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {/* Liste */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>{rows.length === 0 ? k('empty') : k('noMatchFilter')}</Typography>
        ) : (
          <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.platformProduct')}</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>{k('col.ourProduct')}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }}>{k('col.score')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.category')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.source')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.status')}</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(r => {
                const s = shown(r);
                const scope = editScope(r);
                const ref = s.source === 'pending' ? null : s.ref;
                const bg = s.status === 'suggested' ? 'warning.50' : s.status === 'unmatched' ? 'error.50' : undefined;
                return (
                  <TableRow key={rowKey(r)} hover sx={{ bgcolor: bg }}>
                    <TableCell>
                      <Box sx={{ display: 'grid', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: 'text.secondary' }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PLATFORM_COLORS[r.platform] }} />
                          {showAccountLabel ? r.accountLabel : PLATFORM_LABELS[r.platform]}
                        </Box>
                        <Typography fontWeight={700} fontSize={14}>
                          {r.externalName}{r.externalVariant ? ` · ${r.externalVariant}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {k('soldLine', { qty: Math.round(r.soldQuantity), orders: r.orderCount })}
                          {isCompanyView && !viewBranch && r.branchCount > 1 ? ` · ${k('branchCount', { count: r.branchCount })}` : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        fullWidth
                        variant="outlined"
                        color={s.itemId ? 'inherit' : 'primary'}
                        disabled={!scope || s.status === 'ignored'}
                        onClick={(e) => openPicker(e.currentTarget, r)}
                        sx={{ justifyContent: 'space-between', textTransform: 'none', borderStyle: s.itemId ? 'solid' : 'dashed' }}
                      >
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                          {s.status === 'ignored' ? '—' : s.itemName || k('pickProduct')}
                          {ref?.stockCode ? <Box component="span" sx={{ color: 'text.secondary', fontSize: 11, ml: 1 }}>{ref.stockCode}</Box> : null}
                        </Box>
                        <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>▾</Box>
                      </Button>
                      {ref?.notInBranchPriceList && (
                        <Typography variant="caption" color="warning.main" display="block">{k('notInBranchList')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.status === 'ignored' ? '—' : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 42 }}>%{Math.round(s.score)}</Typography>
                          <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden', minWidth: 40 }}>
                            <Box sx={{ height: '100%', width: `${Math.min(100, s.score)}%`, bgcolor: scoreColor(s.score, settings) }} />
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{categoryName(ref?.categoryId ?? (s.source === 'pending' ? pending[rowKey(r)]?.item?.categoryId : null)) || '—'}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        {sourceChip(r)}
                        {!scope && (
                          <Button size="small" onClick={() => setOverrideOpen(prev => new Set(prev).add(rowKey(r)))}>{k('changeHere')}</Button>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{statusChip(r)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })} aria-label={k('more')}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Alt çubuk */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>{k('footerHint')}</Typography>
        <Button onClick={onClose} disabled={saving}>{k('close')}</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving || pendingCount === 0}>
          {saving ? <CircularProgress size={18} color="inherit" /> : pendingCount > 0 ? k('saveCount', { count: pendingCount }) : k('save')}
        </Button>
      </Box>

      {/* Ürün seçici */}
      <Popover open={!!picker} anchorEl={picker?.anchor} onClose={() => setPicker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { width: 380, p: 1.5 } }}>
        {picker && (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {picker.scope === 'branch' ? k('pickForBranch') : k('pickForAccount')}
            </Typography>
            <Autocomplete
              open
              disablePortal
              autoHighlight
              options={pickerOptions}
              loading={pickerLoading}
              filterOptions={(x) => x}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.inventoryItemId === b.inventoryItemId}
              onInputChange={(_, v, reason) => { if (reason === 'input') searchCandidates(v); }}
              onChange={(_, v) => {
                if (v) setRowPending(picker.row, { action: 'map', scope: picker.scope, item: v });
                setPicker(null);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.inventoryItemId}>
                  <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap fontSize={14}>{o.name}</Typography>
                      {o.stockCode && <Typography variant="caption" color="text.secondary">{o.stockCode}</Typography>}
                    </Box>
                    {o.score > 0 && <Typography variant="caption" fontWeight={700} sx={{ color: scoreColor(o.score, settings) }}>%{Math.round(o.score)}</Typography>}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} autoFocus size="small" placeholder={k('searchProduct')} />}
              ListboxProps={{ style: { maxHeight: 320 } }}
              noOptionsText={k('noProduct')}
            />
            <Box sx={{ height: 330 }} />
          </Box>
        )}
      </Popover>

      {/* Satır menüsü */}
      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {menu && (() => {
          const r = menu.row;
          const scope = editScope(r) ?? 'branch';
          const items: React.ReactNode[] = [];
          if (shown(r).status !== 'ignored') {
            items.push(
              <MenuItem key="ignore" onClick={() => { setRowPending(r, { action: 'ignore', scope }); if (scope === 'branch') setOverrideOpen(p => new Set(p).add(rowKey(r))); setMenu(null); }}>
                {k('actionIgnore')}
              </MenuItem>,
            );
          }
          if (pending[rowKey(r)]) {
            items.push(<MenuItem key="undo" onClick={() => { setRowPending(r, null); setMenu(null); }}>{k('actionUndo')}</MenuItem>);
          }
          if (!isCompanyView && r.branch) {
            items.push(<MenuItem key="reset-branch" onClick={() => { setRowPending(r, { action: 'reset', scope: 'branch' }); setMenu(null); }}>{k('actionBackToCompany')}</MenuItem>);
          }
          if ((isCompanyView && !viewBranch) || scope === 'account') {
            const st = r.account?.status;
            if (st === 'manual' || st === 'ignored') {
              items.push(<MenuItem key="reset-account" onClick={() => { setRowPending(r, { action: 'reset', scope: 'account' }); setMenu(null); }}>{k('actionBackToAuto')}</MenuItem>);
            }
          }
          return items;
        })()}
      </Menu>
    </Dialog>
  );
};

export default FoodProductMappingModal;
